import React from 'react';
import { ClientGroup } from '../types/clientGroup';
import { Claim } from '../api/claims';
import { getClaimDisplayStatus } from '../utils/claimGrouping';
import { canSendFirstLoc } from '../utils/bulkSubmission';

interface ClientGroupRowProps {
  group: ClientGroup;
  onToggleExpand: (email: string) => void;
  onSendLOCToAll: (group: ClientGroup) => void;
  onCancelAll: (group: ClientGroup) => void;
  onRequestId: (group: ClientGroup) => void;
  onClaimAction: (action: string, claim: Claim) => void;
  sendingIds: Set<string>;
  actionLoading: Set<string>;
  isMobile?: boolean;
}

const getDocumentCount = (claim: Claim): number => {
  let count = 0;
  if (claim.formOfAuthorityUrl) count++;
  if (claim.damageBasedAgreementUrl) count++;
  if (claim.locDocUrl) count++;
  if (claim.valid8responseUrl) count++;
  if (claim.fileCount && claim.fileCount > 0) count += claim.fileCount;
  return count;
};

export const ClientGroupRow: React.FC<ClientGroupRowProps> = ({
  group,
  onToggleExpand,
  onSendLOCToAll,
  onCancelAll,
  onRequestId,
  onClaimAction,
  sendingIds,
  actionLoading,
  isMobile = false
}) => {
  const hasActiveClaims = group.activeClaims > 0;
  const hasUnsentLOCs = group.claims.some(canSendFirstLoc);

  if (isMobile) {
    return (
      <div className="mobile-client-card">
        <div className="mobile-client-header" onClick={() => onToggleExpand(group.email)}>
          <div className="mobile-client-info">
            <div className="mobile-client-name">{group.clientName}</div>
            <div className="mobile-client-email">{group.email}</div>
          </div>
          <span className={`mobile-expand-icon ${group.expanded ? 'expanded' : ''}`}>
            ▶
          </span>
        </div>

        <div className="mobile-client-stats">
          <div className="mobile-stat">
            <strong>Total Claims:</strong> {group.totalClaims}
            {group.uniqueCreditDates > 0 && (
              <span style={{
                fontSize: '11px',
                marginLeft: '4px',
                color: group.uniqueCreditDates < group.totalClaims ? '#e67e22' : '#6b7280',
                fontWeight: group.uniqueCreditDates < group.totalClaims ? 600 : 400,
              }}>
                ({group.uniqueCreditDates} unique)
              </span>
            )}
          </div>
          <div className="mobile-stat">
            <strong>Active:</strong> {group.activeClaims}
          </div>
          <div className="mobile-stat">
            <strong>LOC Sent:</strong> {group.locSentClaims}
          </div>
          {group.bulkSubmittedClaims > 0 && (
            <div className="mobile-stat">
              <strong style={{ color: '#2e7d32' }}>Bulk Submitted:</strong> {group.bulkSubmittedClaims}
            </div>
          )}
          {group.responseExceptionClaims > 0 && (
            <div className="mobile-stat">
              <strong style={{ color: '#92400e' }}>⚠ Response Exceptions:</strong> {group.responseExceptionClaims}
            </div>
          )}
          <div className="mobile-stat">
            <strong>Cancelled:</strong> {group.cancelledClaims}
          </div>
          {group.auditedClaims > 0 && (
            <div className="mobile-stat" style={{ flex: '1 1 100%' }}>
              <strong style={{ color: '#6b21a8' }}>✓ Audited:</strong> {group.auditedClaims}
            </div>
          )}
        </div>

        <div className="mobile-client-actions">
          {hasUnsentLOCs && (
            <button
              className="batch-action-btn primary"
              onClick={(e) => {
                e.stopPropagation();
                onSendLOCToAll(group);
              }}
            >
              Send LOC to All
            </button>
          )}
          {hasActiveClaims && (
            <button
              className="batch-action-btn danger"
              onClick={(e) => {
                e.stopPropagation();
                onCancelAll(group);
              }}
            >
              Cancel All
            </button>
          )}
        </div>

        {group.expanded && (
          <div className="mobile-claims-list">
            {group.claims.map(claim => (
              <div key={claim.id} className="mobile-claim-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span className="mobile-claim-id">#{claim.id}</span>
                  {claim.isLegacyClaim && (
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      backgroundColor: '#e5e7eb',
                      color: '#6b7280',
                      borderRadius: '4px',
                      fontWeight: 500
                    }}>
                      Legacy
                    </span>
                  )}
                </div>
                <div className="mobile-claim-status">
                  Status: {getClaimDisplayStatus(claim)}
                  {claim.reinstatementDisabled && (
                    <span style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      padding: '1px 5px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      marginLeft: '4px',
                    }}>
                      No Reinstate
                    </span>
                  )}
                </div>
                {claim.craFinanceLender && (
                  <div style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                    {claim.craFinanceLender}
                  </div>
                )}
                {claim.replyCount && claim.replyCount > 0 && (
                  <div style={{ fontSize: '12px', color: '#059669', fontWeight: 500, marginTop: '4px' }}>
                    ✉️ {claim.replyCount} {claim.replyCount === 1 ? 'reply' : 'replies'}
                  </div>
                )}
                {getDocumentCount(claim) > 0 && (
                  <button
                    onClick={() => onClaimAction('docs', claim)}
                    style={{
                      marginTop: '8px',
                      marginBottom: '8px',
                      padding: '6px 12px',
                      backgroundColor: '#01B8C0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    📄 View Documents ({getDocumentCount(claim)})
                  </button>
                )}
                <div className="mobile-claim-actions">
                  <button onClick={() => onClaimAction('details', claim)}>
                    Details
                  </button>
                  {canSendFirstLoc(claim) && (
                    <button
                      onClick={() => onClaimAction('sendLOC', claim)}
                      disabled={sendingIds.has(claim.id)}
                    >
                      {sendingIds.has(claim.id) ? 'Sending...' : 'Send LOC'}
                    </button>
                  )}
                  {(!claim.status || claim.status.toUpperCase() !== 'CANCELLED') && (
                    <button
                      onClick={() => onClaimAction('cancel', claim)}
                      disabled={actionLoading.has(claim.id)}
                    >
                      Cancel
                    </button>
                  )}
                  {claim.status && claim.status.toUpperCase() === 'CANCELLED' && claim.reinstatementBlock && (
                    <>
                      <span title={claim.reinstatementBlock.message} style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                        <button
                          disabled
                          style={{ backgroundColor: '#e5e7eb', borderColor: '#e5e7eb', color: '#9ca3af', pointerEvents: 'none' }}
                        >
                          Reinstate
                        </button>
                      </span>
                      <span style={{ flexBasis: '100%', fontSize: '11px', color: '#b91c1c' }}>
                        {claim.reinstatementBlock.message}
                      </span>
                    </>
                  )}
                  {claim.status && claim.status.toUpperCase() === 'CANCELLED' && !claim.reinstatementBlock && !claim.reinstatementDisabled && (
                    <button
                      onClick={() => onClaimAction('reinstate', claim)}
                      disabled={actionLoading.has(claim.id)}
                    >
                      Reinstate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <tr className={`client-group-row ${group.expanded ? 'expanded' : ''}`}>
        <td>
          <div className="client-info">
            <span 
              className={`expand-icon ${group.expanded ? 'expanded' : ''}`}
              onClick={() => onToggleExpand(group.email)}
            >
              ▶
            </span>
            <div>
              <div className="client-name">{group.clientName}</div>
              <div className="client-email">{group.email}</div>
            </div>
          </div>
        </td>
        <td>
          <div>
            <span>{group.totalClaims}</span>
            {group.uniqueCreditDates > 0 && (
              <div style={{
                fontSize: '11px',
                color: group.uniqueCreditDates < group.totalClaims ? '#e67e22' : '#6b7280',
                fontWeight: group.uniqueCreditDates < group.totalClaims ? 600 : 400,
              }}>
                {group.uniqueCreditDates} unique
              </div>
            )}
          </div>
        </td>
        <td>
          <div className="status-badges">
            {group.activeClaims > 0 && (
              <span className="status-badge active">
                {group.activeClaims} Active
              </span>
            )}
            {group.locSentClaims > 0 && (
              <span className="status-badge loc-sent">
                {group.locSentClaims} LOC Sent
              </span>
            )}
            {group.bulkSubmittedClaims > 0 && (
              <span className="status-badge" style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }}>
                {group.bulkSubmittedClaims} Bulk Submitted
              </span>
            )}
            {group.responseExceptionClaims > 0 && (
              <span
                className="status-badge"
                title="Lender response received with an exception outcome (multi-rep, withdrawn, no records found…) — open the claim for the detail"
                style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}
              >
                ⚠ {group.responseExceptionClaims} Response Exception{group.responseExceptionClaims > 1 ? 's' : ''}
              </span>
            )}
            {group.cancelledClaims > 0 && (
              <span className="status-badge cancelled">
                {group.cancelledClaims} Cancelled
              </span>
            )}
            {group.completedClaims > 0 && (
              <span className="status-badge completed">
                {group.completedClaims} Completed
              </span>
            )}
            {group.auditedClaims > 0 && (
              <span className="status-badge audited" style={{ background: '#f3e8ff', color: '#6b21a8', border: '1px solid #e9d5ff' }}>
                ✓ {group.auditedClaims} Audited
              </span>
            )}
          </div>
        </td>
        <td>
          <div className="batch-actions">
            {hasUnsentLOCs && (
              <button
                className="batch-action-btn primary"
                onClick={() => onSendLOCToAll(group)}
              >
                Send LOC to All
              </button>
            )}
            {hasActiveClaims && (
              <button
                className="batch-action-btn danger"
                onClick={() => onCancelAll(group)}
              >
                Cancel All Claims
              </button>
            )}
            {hasActiveClaims && group.idVerificationStatus !== 'verified' && (
              <button
                className="batch-action-btn"
                style={{ backgroundColor: '#9333ea', color: 'white' }}
                onClick={() => onRequestId(group)}
                title="Send photo ID verification request to client"
              >
                Request ID
              </button>
            )}
          </div>
        </td>
      </tr>

      {group.expanded && group.claims.map(claim => (
        <tr key={claim.id} className="claim-row">
          <td colSpan={2}>
            <div style={{ paddingLeft: '40px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500, color: '#01B8C0' }}>#{claim.id}</span>
                {claim.isLegacyClaim && (
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    backgroundColor: '#e5e7eb',
                    color: '#6b7280',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    Legacy
                  </span>
                )}
              </div>
              <div style={{ fontSize: '13px', color: '#666' }}>
                {claim.firstName} {claim.lastName}
              </div>
            </div>
          </td>
          <td>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontWeight: 500 }}>
                {getClaimDisplayStatus(claim)}
                {claim.reinstatementDisabled && (
                  <span style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontSize: '10px',
                    marginLeft: '4px',
                  }}>
                    No Reinstate
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {claim.submissionSent ? (
                  <>Submitted: {claim.submissionSentDate ? new Date(claim.submissionSentDate).toLocaleDateString() : 'N/A'}</>
                ) : (() => {
                  // Prefer a live send date, fall back to the preserved one when cancel
                  // cleared locSentDate, then fall back to the boolean withdrawal marker
                  // for legacy claims cancelled before locPreviousSentDate was introduced.
                  const sentDate = claim.locSentDate || claim.locPreviousSentDate;
                  if (sentDate) {
                    return <>LOC: {new Date(sentDate).toLocaleDateString()}</>;
                  }
                  if (claim.locWithdrawalSentToLender) {
                    return <>LOC: Sent (date not retained)</>;
                  }
                  return <>LOC: Not Sent</>;
                })()}
              </div>
              {claim.craFinanceLender && (
                <div style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                  {claim.craFinanceLender}
                </div>
              )}
              {claim.replyCount && claim.replyCount > 0 && (
                <div style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>
                  ✉️ {claim.replyCount} {claim.replyCount === 1 ? 'reply' : 'replies'}
                </div>
              )}
            </div>
          </td>
          <td>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {getDocumentCount(claim) > 0 ? (
                <button
                  onClick={() => onClaimAction('docs', claim)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#01B8C0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  📄 Docs ({getDocumentCount(claim)})
                </button>
              ) : null}
              <button
                className="batch-action-btn"
                onClick={() => onClaimAction('details', claim)}
                style={{ padding: '5px 10px', fontSize: '12px' }}
              >
                Details
              </button>
              {canSendFirstLoc(claim) && (
                <button
                  className="batch-action-btn primary"
                  onClick={() => onClaimAction('sendLOC', claim)}
                  disabled={sendingIds.has(claim.id)}
                  style={{ padding: '5px 10px', fontSize: '12px' }}
                >
                  {sendingIds.has(claim.id) ? 'Sending...' : 'Send LOC'}
                </button>
              )}
              {(!claim.status || claim.status.toUpperCase() !== 'CANCELLED') && (
                <button
                  className="batch-action-btn danger"
                  onClick={() => onClaimAction('cancel', claim)}
                  disabled={actionLoading.has(claim.id)}
                  style={{ padding: '5px 10px', fontSize: '12px' }}
                >
                  Cancel
                </button>
              )}
              {claim.status && claim.status.toUpperCase() === 'CANCELLED' && claim.reinstatementBlock && (
                <>
                  {/* The tooltip sits on a wrapper: some browsers show none on a disabled button. */}
                  <span title={claim.reinstatementBlock.message} style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                    <button
                      className="batch-action-btn"
                      disabled
                      style={{
                        padding: '5px 10px',
                        fontSize: '12px',
                        backgroundColor: '#e5e7eb',
                        borderColor: '#e5e7eb',
                        color: '#9ca3af',
                        pointerEvents: 'none',
                      }}
                    >
                      Reinstate
                    </button>
                  </span>
                  <span style={{ flexBasis: '100%', fontSize: '11px', color: '#b91c1c' }}>
                    {claim.reinstatementBlock.message}
                  </span>
                </>
              )}
              {claim.status && claim.status.toUpperCase() === 'CANCELLED' && !claim.reinstatementBlock && !claim.reinstatementDisabled && (
                <button
                  className="batch-action-btn"
                  onClick={() => onClaimAction('reinstate', claim)}
                  disabled={actionLoading.has(claim.id)}
                  style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#28a745', color: 'white' }}
                >
                  Reinstate
                </button>
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};